import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query/react';
import TokenManager from '../utils/tokenManager';
import { BASE_URL } from '../constants';
import snackbar from '../utils/snackbar';
import { setServiceIdData } from './profile/actionSlice';
import serviceIdMessags from '../utils/serviceIdMessags';
import { getClientType } from '@/pwa/pwaDetection';
import { tokenRefreshService } from '@/utils/tokenRefreshService';
import { LOG_STYLES } from '@/utils/consoleColors';
import { AUTH_FLOW_ENDPOINTS } from '@/constants/auth';
import { hasVpnNoticeHost, openVpnNotice } from '@/components/VpnNotice/bus';
import { withCaptcha } from './captcha/withCaptcha';

/**
 * Base query configuration for RTK Query
 * - Adds Authorization header with access token
 * - Adds X-Client-Type header for PWA detection
 * - 60 second timeout for requests
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  timeout: 60000,
  prepareHeaders: async (headers: any) => {
    // A sign-in flow in progress authenticates with its pending token; otherwise the session's
    // (from memory or Service Worker).
    const token =
      TokenManager.getPending() ?? (await TokenManager.getAccessToken());

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    // Add client type header (web or PWA)
    const clientType = getClientType();
    if (clientType === 'pwa') {
      headers.set('X-Client-Type', 'mobile');
    }

    return headers;
  }
});

/**
 * The raw fetch, plus the gateway captcha on /api/v2 routes: a 428 or a
 * rejected token waits for <CaptchaHost/> and retries, so everything below
 * sees only the final answer.
 */
const baseQuery = withCaptcha(rawBaseQuery);

/**
 * Enhanced base query with automatic token refresh
 *
 * Handles:
 * - 401 Unauthorized: Auto-refresh token and retry request
 * - Timeout errors: Show user-friendly message
 * - 406 Not Acceptable: VPN/proxy warning
 * - 499 Custom: Service ID verification
 * - 428 / 400 captcha challenges (in baseQuery, see services/captcha)
 */
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  // Handle 401 Unauthorized - try to refresh token. Not for the sign-in flows: there a 401 is a
  // wrong password/code, which the page shows itself.
  if (
    result.error &&
    result.error.status === 401 &&
    !AUTH_FLOW_ENDPOINTS.has(api.endpoint)
  ) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('%c[API] Got 401, attempting refresh...', LOG_STYLES.info);
    }

    try {
      // Try to refresh token using HttpOnly cookie
      await tokenRefreshService.refresh();

      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '%c[API] ✅ Refresh successful, retrying request...',
          LOG_STYLES.success
        );
      }

      // Retry original request with new token
      result = await baseQuery(args, api, extraOptions);
    } catch (refreshError: any) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(
          '%c[API] ❌ Refresh failed:',
          LOG_STYLES.error,
          refreshError
        );
      }

      // Refresh failed - logout user
      if (refreshError.status === 401 || refreshError.status === 400) {
        tokenRefreshService.logout(true, 'نشست شما منقضی شده است.');
      }
    }
  }

  // Handle timeout errors
  if (result.error?.status === 'TIMEOUT_ERROR') {
    snackbar.error(
      'متاسفانه به دلیل اتصال ضعیف اینترنت، درخواست شما پاسخی در زمان مناسب نیافت. لطفاً اتصال خود را بررسی و دوباره تلاش کنید.',
      {
        autoHideDuration: 3000
      }
    );
  }

  // Handle 406 - the backend refuses IPs outside Iran. The deposit and
  // withdrawal pages mount a VPN notice sheet to take it; everywhere else keeps
  // the toast. Either way the generic error toast is suppressed.
  if (result.error && result.error.status === 406) {
    if (hasVpnNoticeHost()) {
      openVpnNotice();
    } else {
      snackbar.black('لطفا فیلتر شکن خود را خاموش نمایید.');
    }
    (result.error as any).customHandledSnackbar = true;
  }

  // Handle 499 - Service ID verification
  if (result.error && result.error.status === 499) {
    api.dispatch(
      setServiceIdData({
        // @ts-ignore
        id: result.error.data.data.service_id,
        args,
        api,
        extraOptions,
        hint: serviceIdMessags((args as FetchArgs)?.url)
      })
    );
  }

  return result;
};

/**
 * RTK Query API instance
 * All API endpoints are defined in separate slice files
 */
export const api = createApi({
  baseQuery: baseQueryWithReauth,
  refetchOnReconnect: true,
  tagTypes: [
    'BasicInfo',
    'orderHistory',
    'getMarkets',
    'getTickers',
    'getAggTrades',
    'GetAccountingBalanceFilter',
    'getOpenOrders',
    'getBankAccount',
    'getTickets',
    'getTicket',
    'userActiveDevices',
    'getOtcMarketTicker',
    'PriceAlerts',
    'Notifications',
    'WithdrawalWhitelist',
    'RewardCampaigns',
    'RewardCampaign',
    'RewardTasks',
    'RewardTask',
    'RewardHistory',
    'RewardLottery',
    'SmallBalanceAssets',
    'SmallBalanceHistory',
    'UserAssets'
  ],
  endpoints: () => ({})
});
